/**
 * GW-6533: Branded `OrgId` type and runtime guard.
 * GW-6560: Tightened to UUID-or-"global-admin-org" only (mirrors GW-6559 backend change, SHA 2b1332c).
 *
 * Provides:
 *   - `OrgId` — a nominal/branded type used by API clients to confirm a value
 *     has been validated before being interpolated into a URL.
 *   - `assertOrgId(value)` — a TypeScript assertion guard that throws
 *     `InvalidOrgIdError` when `value` cannot be a syntactically valid
 *     organization id.
 *   - `isOrgId(value)` — a convenience predicate (same logic, no throw).
 *   - `InvalidOrgIdError` — a named Error subclass so Sentry groups these
 *     thrown-pre-flight regressions distinctly from network 4xx (`ApiError`).
 *
 * Validator parity: mirrors the gw-smart-tokens orgIDValidPattern tightened in
 * GW-6559. A valid orgId is either:
 *   1. A UUID (any version) matching the canonical 8-4-4-4-12 hex format, or
 *   2. The literal string "global-admin-org" used for platform-wide admin
 *      operations.
 * All other forms — including legacy kebab-case placeholders such as "org-1"
 * and "org-123" — are rejected.
 */

declare const OrgIdBrand: unique symbol;
export type OrgId = string & { readonly [OrgIdBrand]: true };

/**
 * Accepts a canonical UUID (any version, 8-4-4-4-12 hex format) or the
 * literal sentinel "global-admin-org". All other forms are rejected.
 */
const ORG_ID_UUID_PATTERN =
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

export const GLOBAL_ADMIN_ORG_ID = 'global-admin-org';

/**
 * GW-9488 (part of GW-9477): the canonical display name for the Global Admin Org
 * sentinel. This is the single TypeScript-side source of truth for the name —
 * mirrors the shared backend constant (`ORG_NAME` in gw-migrations
 * `017_reassert_global_admin_org.ts` and the gw-search-api org resolver). The
 * retired display string "Global Admin Org" must not appear in shipped code or
 * fixtures. Consumers (tests, UI) should reference this constant rather than
 * inlining the literal.
 */
export const GLOBAL_ADMIN_ORG_NAME = 'Global Administration';

/**
 * Returns true when `value` is a valid `OrgId` (UUID or "global-admin-org").
 * Useful for conditional branches that should NOT throw (e.g. when react-query
 * needs to decide whether to enable a query).
 */
export function isOrgId(value: unknown): value is OrgId {
  if (typeof value !== 'string') return false;
  return value === GLOBAL_ADMIN_ORG_ID || ORG_ID_UUID_PATTERN.test(value);
}

/**
 * Thrown by `assertOrgId` when its input cannot be a syntactically valid
 * organization id. Named distinctly from `ApiError` so the Sentry fingerprint
 * helper can group these as `frontend-bug` rather than `api-error`.
 */
export class InvalidOrgIdError extends Error {
  /** Original input (stringified, truncated) — useful for log context. */
  public readonly received: string;

  constructor(received: unknown) {
    const receivedStr =
      typeof received === 'string'
        ? received
        : received === undefined
          ? 'undefined'
          : received === null
            ? 'null'
            : typeof received;
    const truncated = receivedStr.length > 64 ? `${receivedStr.slice(0, 61)}...` : receivedStr;
    super(`Invalid orgId: ${JSON.stringify(truncated)}`);
    this.name = 'InvalidOrgIdError';
    this.received = truncated;
  }
}

/**
 * Runtime guard. Throws `InvalidOrgIdError` if the value cannot be a
 * syntactically valid organization id.
 *
 * After a successful call the input is narrowed to `OrgId`, so calling code
 * can use it as an `OrgId` without a re-cast.
 */
export function assertOrgId(value: unknown): asserts value is OrgId {
  if (!isOrgId(value)) {
    throw new InvalidOrgIdError(value);
  }
}
